import type { TEmitterTypeValueType } from '@semios/app-platform-values-helpers-and-types'
import { enumToValueType } from '@semios/app-platform-values-helpers-and-types'
import { EllipsisTooltip } from 'App/Map/PanelDetails/OverviewCharts/EllipsisTooltip'
import type { CurrentValuesDataInfo } from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import { lastDays, periodTitle, sinceFirstOf } from 'App/Map/PanelDetails/OverviewCharts/_utils/columnTitles'
import { renderAppliedAmount } from 'App/Map/PanelDetails/OverviewCharts/_utils/renderAppliedAmount'
import type { TColumnContext, TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import { IrrigationCurrentStatusBadge } from 'App/Map/PanelDetails/SummaryGridTable/_utils/by-domain/irrigationActivity/_utils/IrrigationCurrentStatusBadge/IrrigationCurrentStatusBadge'
import type { TIrrigationDeviceStatus } from 'App/Map/types'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import { decamelize } from 'utils/decamelize'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'

const getDeviceLabel = (pointName: string | undefined, valueType: TEmitterTypeValueType) =>
  `${pointName} (${decamelize(mapApiEmitterTypeToLabel(enumToValueType[valueType]))})`

const getSortLabel = (record: CurrentValuesDataInfo) => {
  const matchingKey = Object.keys(record).find((key) => key.endsWith('_ms'))

  if (!matchingKey) return null

  const valueType = (matchingKey.split('_ms')[0].split('_').pop() || '') as TEmitterTypeValueType

  return getDeviceLabel(record.pointName, valueType)
}

const appliedColumn = (context: TColumnContext, prefix: string, period: string, width: number): TColumn => ({
  title: <EllipsisTooltip fullText={periodTitle(translate.phrases.banyanApp('Applied'), period)} />,
  dataIndex: `${prefix}_equipment_ms`,
  key: `${prefix}_equipment_ms`,
  width,
  render: (_: number, record: CurrentValuesDataInfo) => {
    const irrigationKey = Object.keys(record).find((key) => key.startsWith(prefix))

    return irrigationKey
      ? renderAppliedAmount(record, irrigationKey, context)
      : translate.phrases.templates('-')
  },
})

export const irrigationActivityColumns: TValueGroupColumns = {
  getColumns: (context) => [
    {
      title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Device')} />,
      dataIndex: 'device',
      key: 'device',
      width: 350,
      render: (value: TEmitterTypeValueType, record: CurrentValuesDataInfo) => {
        if (!value) return translate.phrases.banyanApp('Unnamed Device')

        return <EllipsisTooltip fullText={getDeviceLabel(record.pointName, value)} />
      },
      sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
        const aLabel = getSortLabel(a)
        const bLabel = getSortLabel(b)

        if (aLabel === null && bLabel === null) return 0

        if (aLabel === null) return 1

        if (bLabel === null) return -1

        return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' })
      },
    },
    {
      title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Status')} />,
      dataIndex: 'irrigationIsOn_equipment_status',
      key: 'irrigationIsOn_equipment_status',
      width: 150,
      render: (_: string, record: CurrentValuesDataInfo) => {
        const statusKey = Object.keys(record).find((key) => key.startsWith('irrigationIsOn_'))
        const status = record[statusKey as keyof CurrentValuesDataInfo] as TIrrigationDeviceStatus

        return (
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '12px',
              width: 'fit-content',
            }}
          >
            <IrrigationCurrentStatusBadge
              currentValueStatus={!statusKey || status === null ? 'unknown' : status}
            />
          </div>
        )
      },
    },
    appliedColumn(context, 'irrigationActivity_todaySoFarSum', translate.phrases.banyanApp('Today'), 150),
    appliedColumn(context, 'irrigationActivity_previous7DaysSum', lastDays(7), 200),
    appliedColumn(context, 'irrigationActivity_previous30DaysSum', lastDays(30), 200),
    ...(context.isSouthernHemisphere
      ? [
          appliedColumn(context, 'irrigationActivity_sinceApr1Sum', sinceFirstOf('Apr'), 200),
          appliedColumn(context, 'irrigationActivity_sinceJul1Sum', sinceFirstOf('Jul'), 200),
        ]
      : [
          appliedColumn(context, 'irrigationActivity_sinceJan1Sum', sinceFirstOf('Jan'), 200),
          appliedColumn(context, 'irrigationActivity_sinceOct1Sum', sinceFirstOf('Oct'), 200),
        ]),
  ],
}
